import { CSSProperties, FormEvent, useEffect, useState } from "react";

type Audience = "distribution" | "partners";

const tourismSource = "https://library.cystat.gov.cy/NEW/Tourism%20Statistics-2024-EN-220725.pdf";

const tourismStats = [
  ["4.04m", "tourist arrivals", "A record-scale audience travelled to Cyprus in 2024."],
  ["81.1%", "travelled for holidays", "Most visitors arrived with time and intent to experience the island."],
  ["€3.21bn", "tourism revenue", "Visitor spending reached more than three billion euro in 2024."],
  ["€794", "average spend", "The average expenditure per tourist across their Cyprus visit."],
] as const;

const distributionPartnerRoles = [
  {
    id: "airport",
    label: "Airport",
    title: "Own the first Cyprus moment.",
    description:
      "Place TravelHeroes where the island welcomes the world. A selected airport presence turns arrival attention into instant local access.",
    benefits: ["Strong visibility at arrival", "A better destination welcome", "Measurable activation at scale"],
    mockup: "Airport arrival activation",
  },
  {
    id: "airline",
    label: "Airline",
    title: "Extend the journey beyond the flight.",
    description:
      "Give passengers privileged access to Cyprus before they land. TravelHeroes adds destination value without adding operational complexity.",
    benefits: ["Reach travellers before arrival", "Add a valuable passenger benefit", "Stay relevant beyond the flight"],
    mockup: "Pre-arrival and onboard activation",
  },
  {
    id: "transportation",
    label: "Transportation",
    title: "Turn movement into discovery.",
    description:
      "Meet visitors while their plans are still open. TravelHeroes makes every pickup, transfer and handover more valuable.",
    benefits: ["Add value at pickup or handover", "Improve the passenger experience", "Connect journeys with local discovery"],
    mockup: "Mobility handover activation",
  },
  {
    id: "tourist-office",
    label: "Tourist office",
    title: "Scale trusted local guidance.",
    description:
      "Offer a curated digital layer that helps visitors act on inspiration. TravelHeroes extends trusted guidance beyond the desk.",
    benefits: ["Guide visitors consistently", "Spotlight the local economy", "See clear engagement signals"],
    mockup: "Visitor information activation",
  },
  {
    id: "sponsor",
    label: "Sponsor",
    title: "Put your brand behind better travel.",
    description:
      "Support a service designed to improve the Cyprus visitor experience. Selected sponsors earn meaningful visibility by enabling real value.",
    benefits: ["Build positive visitor association", "Reach a high-value audience", "Make participation measurable"],
    mockup: "Sponsored access moment",
  },
  {
    id: "host",
    label: "Host",
    title: "Give every guest a valuable extra.",
    description:
      "Selected hosts can make TravelHeroes a privileged part of the welcome, without maintaining their own recommendation system.",
    benefits: ["Enhance the guest experience", "Keep the handover simple", "Encourage more local discovery"],
    mockup: "Guest welcome activation",
  },
] as const;

const pageContent = {
  distribution: {
    eyebrow: "For Distribution Partners",
    title: "Turn trusted visitor reach into local discovery.",
    intro:
      "TravelHeroes works with organisations that can introduce visitors to curated places, experiences and services across Cyprus.",
    points: [
      ["Add value to the visitor journey", "Offer useful local inspiration through a channel visitors already recognise and trust."],
      ["Make activation simple", "Give visitors a clear route from one introduction to full TravelHeroes access."],
      ["Support the local visitor economy", "Help visitor attention reach more of the places and people that make Cyprus memorable."],
    ],
    steps: [
      "Tell us where your organisation connects with visitors.",
      "Identify the strongest activation points and the simplest route into TravelHeroes.",
      "Agree a focused collaboration and prepare the visitor journey together.",
    ],
    journey: {
      eyebrow: "The activation journey",
      title: "From trusted introduction to active discovery.",
      intro: "TravelHeroes can meet visitors through the channels already shaping their trip, then give them a simple route into the local network.",
      stages: [
        ["01", "Introduce", "A trusted visitor touchpoint presents TravelHeroes at a useful moment."],
        ["02", "Activate", "The visitor scans or follows a simple digital route and completes access."],
        ["03", "Discover", "Relevant places, experiences and services become easier to explore."],
        ["04", "Measure", "Clear activation and engagement signals show how the channel is performing."],
      ],
    },
    detail: {
      eyebrow: "Built for trusted reach",
      title: "Useful for visitors. Flexible for your organisation.",
      items: [
        ["A natural visitor touchpoint", "Introduce local discovery through a moment that already belongs in the visitor journey."],
        ["A curated local layer", "Give visitors a useful starting point without asking your organisation to maintain every recommendation."],
        ["A flexible rollout", "Choose the channels, locations and activation format that make sense before anything is launched."],
        ["A measurable destination story", "Connect distribution activity with activation and engagement signals that can be reviewed together."],
      ],
    },
    formIntro: "Tell us where your organisation reaches visitors and how you would like to explore TravelHeroes.",
  },
  partners: {
    eyebrow: "For Local Partners",
    title: "Be discovered by visitors ready to explore.",
    intro:
      "TravelHeroes is building a curated network of Cyprus experiences and visitor-relevant businesses.",
    points: [
      ["Reach visitors at the right moment", "Be present when visitors are deciding what to see, do, taste and experience."],
      ["Join a curated network", "Stand alongside businesses and experiences selected for their relevance to visitors."],
      ["Start with a clear conversation", "We discuss fit, the visitor offer and onboarding before any collaboration is agreed."],
    ],
    steps: [
      "Tell us about your business, experience or visitor service.",
      "We review the fit with the TravelHeroes network and visitor journey.",
      "If there is a strong match, we shape the collaboration and onboarding together.",
    ],
    journey: {
      eyebrow: "The discovery journey",
      title: "Be present between inspiration and action.",
      intro: "TravelHeroes connects local partners with visitors through trusted distribution channels, at moments when plans are still being made.",
      stages: [
        ["01", "Visitor activates", "A trusted distribution partner introduces the visitor to TravelHeroes."],
        ["02", "Visitor discovers", "TravelHeroes makes relevant local options easier to find."],
        ["03", "Your offer stands out", "A clear, visitor-relevant proposition helps visitors understand the value quickly."],
        ["04", "A visit begins", "Discovery can turn into a real-world experience with your business."],
      ],
    },
    detail: {
      eyebrow: "A strong partner fit",
      title: "Made for businesses that add to the Cyprus experience.",
      items: [
        ["Relevant to visitors", "Your offer gives visitors a meaningful reason to see, do, taste or experience more of Cyprus."],
        ["Clear and easy to understand", "Visitors can quickly see what makes the experience useful, distinctive or memorable."],
        ["Ready to welcome visitors", "The experience behind the listing is dependable and prepared for an international audience."],
        ["Part of the destination", "Your business contributes something genuinely local to the wider Cyprus journey."],
      ],
    },
    formIntro: "Tell us what you offer and why it would be valuable to visitors in Cyprus.",
  },
} as const;

function DistributionPartnerSelector() {
  const [selectedRole, setSelectedRole] = useState<(typeof distributionPartnerRoles)[number]["id"]>("airport");
  const activeRole = distributionPartnerRoles.find((role) => role.id === selectedRole) ?? distributionPartnerRoles[0];

  return (
    <section className="partner-selector" aria-labelledby="partner-selector-title" data-reveal>
      <div className="partner-selector__heading">
        <div>
          <p className="eyebrow">Choose your distribution role</p>
          <h2 id="partner-selector-title">Who are you?</h2>
        </div>
        <p>
          Cyprus welcomed 4.04 million tourists in 2024. TravelHeroes is selecting the channels that can turn that scale into millions of meaningful discovery moments.
        </p>
      </div>

      <div className="partner-tabs" role="tablist" aria-label="Distribution partner types">
        {distributionPartnerRoles.map((role) => (
          <button
            key={role.id}
            id={`partner-tab-${role.id}`}
            type="button"
            role="tab"
            aria-selected={selectedRole === role.id}
            aria-controls={`partner-panel-${role.id}`}
            tabIndex={selectedRole === role.id ? 0 : -1}
            onClick={() => setSelectedRole(role.id)}
          >
            {role.label}
          </button>
        ))}
      </div>

      <div
        key={activeRole.id}
        id={`partner-panel-${activeRole.id}`}
        className="partner-selector__panel"
        role="tabpanel"
        aria-labelledby={`partner-tab-${activeRole.id}`}
      >
        <div className="partner-selector__copy">
          <span className="selective-badge">Selected distribution partner</span>
          <h3>{activeRole.title}</h3>
          <p>{activeRole.description}</p>
          <ul>
            {activeRole.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
          </ul>
          <p className="partner-selector__note">A place in the network is more than an advertising slot. It is a trusted role in how visitors discover Cyprus.</p>
        </div>

        <div className="mockup-placeholder" aria-label={`${activeRole.mockup} mockup placeholder`}>
          <div className="mockup-placeholder__frame">
            <span>Mockup placeholder</span>
            <strong>{activeRole.mockup}</strong>
            <small>Role-specific visual coming next</small>
          </div>
        </div>
      </div>
    </section>
  );
}

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/" aria-label="TravelHeroes Cyprus home">
        <img src="/travelheroes-logo.svg" alt="TravelHeroes" />
        <span>Cyprus</span>
      </a>
      <nav aria-label="Primary navigation">
        <a href="/distribution-partners">For Distribution Partners</a>
        <a href="/partners">For Local Partners</a>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <a href="/" aria-label="TravelHeroes Cyprus home">
          <img src="/travelheroes-logo-white.svg" alt="TravelHeroes" />
        </a>
        <div className="footer-links">
          <a href="/distribution-partners">For Distribution Partners</a>
          <a href="/partners">For Local Partners</a>
        </div>
        <div className="footer-meta">
          <p>TravelHeroes Cyprus</p>
          <a href={tourismSource} target="_blank" rel="noreferrer">Tourism data: CYSTAT 2024</a>
          <span>Photography: Unsplash &amp; Wikimedia Commons</span>
        </div>
      </div>
    </footer>
  );
}

function StatBand() {
  return (
    <section className="stat-band" aria-label="Cyprus tourism in numbers" data-reveal>
      <div className="stat-band__intro">
        <p className="eyebrow">Cyprus in numbers</p>
        <h2>A big visitor economy, full of local moments.</h2>
      </div>
      <div className="stat-grid">
        {tourismStats.map(([value, label, description]) => (
          <article key={label}>
            <strong>{value}</strong>
            <h3>{label}</h3>
            <p>{description}</p>
          </article>
        ))}
      </div>
      <p className="data-source">Official 2024 tourism statistics. <a href={tourismSource} target="_blank" rel="noreferrer">View the CYSTAT source</a></p>
    </section>
  );
}

function DestinationGallery() {
  return (
    <section className="destination-section" aria-labelledby="destination-title" data-reveal>
      <div className="section-heading">
        <p className="eyebrow">One island, many reasons to explore</p>
        <h2 id="destination-title">The best recommendations feel unmistakably local.</h2>
        <p>From open coastline to mountain villages and long tables, Cyprus gives every visitor more to discover. TravelHeroes is being built to make that story easier to enter.</p>
      </div>
      <div className="photo-grid">
        <figure className="photo-card photo-card--wide">
          <img src="/images/cyprus-coast.jpg" alt="Turquoise coastline near Paphos, Cyprus" loading="lazy" />
          <figcaption><span>01</span><strong>Coast</strong><small>Turn a free afternoon into a place worth remembering.</small></figcaption>
        </figure>
        <figure className="photo-card">
          <img src="/images/cyprus-village.jpg" alt="Traditional hillside houses in Kalopanayiotis, Cyprus" loading="lazy" />
          <figcaption><span>02</span><strong>Culture</strong><small>Bring village life and heritage closer to the visitor journey.</small></figcaption>
        </figure>
        <figure className="photo-card">
          <img src="/images/cyprus-meze.jpg" alt="Fish mezze served in Polis, Cyprus" loading="lazy" />
          <figcaption><span>03</span><strong>Taste</strong><small>Help local flavours become part of the journey.</small></figcaption>
        </figure>
      </div>
    </section>
  );
}

const discoveryMoments = ["Taste", "Explore", "Unwind", "Discover", "Meet local", "Go further"] as const;

function DiscoveryRibbon() {
  return (
    <div className="discovery-ribbon" aria-label="Ways to experience Cyprus">
      <div className="discovery-ribbon__track">
        {[...discoveryMoments, ...discoveryMoments].map((moment, index) => (
          <span key={`${moment}-${index}`} aria-hidden={index >= discoveryMoments.length}>
            {moment}<i>✦</i>
          </span>
        ))}
      </div>
    </div>
  );
}

function DataStory({ audience }: { audience: Audience }) {
  if (audience === "distribution") {
    const regions = [
      ["Pafos", 31.5], ["Ayia Napa", 15.9], ["Larnaka", 14.2],
      ["Limassol", 12.4], ["Paralimni", 11.4], ["Other areas", 14.6],
    ] as const;
    return (
      <section className="data-story" aria-labelledby="data-story-title" data-reveal>
        <div className="data-story__copy">
          <p className="eyebrow">A nationwide visitor opportunity</p>
          <h2 id="data-story-title">Reach visitors across the island.</h2>
          <p>In 2024, more than four in five tourists came to Cyprus for holidays. Visitor demand extended across the island’s principal tourism regions—creating many opportunities for trusted channels to open the door to local discovery.</p>
          <div className="big-ratio"><strong>81.1%</strong><span>of tourists travelled for holidays</span></div>
        </div>
        <div className="chart-card">
          <div className="chart-title"><strong>Regional visitor distribution</strong><span>Share of tourists · 2024</span></div>
          <div className="bar-chart">
            {regions.map(([name, value]) => (
              <div className="bar-row" key={name}>
                <span>{name}</span><div><i style={{ "--bar": `${value}%` } as CSSProperties} /></div><strong>{value}%</strong>
              </div>
            ))}
          </div>
          <p className="data-source"><a href={tourismSource} target="_blank" rel="noreferrer">Source: CYSTAT Tourism Statistics 2024</a></p>
        </div>
      </section>
    );
  }

  const markets = [
    ["United Kingdom", 34], ["Israel", 10.5], ["Poland", 8.3],
    ["Germany", 5.7], ["Greece", 4.5], ["Sweden", 3.7],
  ] as const;
  return (
    <section className="data-story data-story--partners" aria-labelledby="data-story-title" data-reveal>
      <div className="data-story__copy">
        <p className="eyebrow">A broad visitor audience</p>
        <h2 id="data-story-title">Local experiences meet visitors from many markets.</h2>
        <p>Cyprus welcomed 4.04 million tourists in 2024. The United Kingdom was the largest source market, while visitors from Israel, Poland, Germany, Greece, Sweden and many other countries added to a varied international audience.</p>
        <div className="donut-wrap">
          <div className="donut" aria-label="34 percent of tourists came from the United Kingdom"><span><strong>34%</strong>UK share</span></div>
          <p>Two out of three tourists came from markets beyond the UK—showing how varied the visitor audience can be.</p>
        </div>
      </div>
      <div className="chart-card chart-card--blue">
        <div className="chart-title"><strong>Leading source markets</strong><span>Share of tourist traffic · 2024</span></div>
        <div className="market-chart">
          {markets.map(([name, value]) => (
            <div className="market-column" key={name}>
              <div><i style={{ "--height": `${(value / 34) * 100}%` } as CSSProperties} /></div>
              <strong>{value}%</strong><span>{name}</span>
            </div>
          ))}
        </div>
        <p className="data-source"><a href={tourismSource} target="_blank" rel="noreferrer">Source: CYSTAT Tourism Statistics 2024</a></p>
      </div>
    </section>
  );
}

function PartnerProductExperience() {
  const [redeemed, setRedeemed] = useState(false);
  const [metricPulse, setMetricPulse] = useState(0);
  const [metrics, setMetrics] = useState({ views: 1248, saves: 286, redemptions: 94, conversion: 7.5 });
  const [activity, setActivity] = useState([42, 58, 49, 78, 64, 92, 73]);

  function toggleRedemption() {
    if (redeemed) {
      setRedeemed(false);
      return;
    }
    setRedeemed(true);
    setMetricPulse((current) => current + 1);
    setMetrics((current) => ({
      views: current.views + 1,
      saves: current.saves + 1,
      redemptions: current.redemptions + 1,
      conversion: Number((current.conversion + 0.1).toFixed(1)),
    }));
    setActivity((current) => current.map((value, index) => index === current.length - 1 ? Math.min(value + 8, 98) : value));
  }

  return (
    <section className="product-experience" aria-labelledby="product-experience-title" data-reveal>
      <div className="product-orb product-orb--one" aria-hidden="true" />
      <div className="product-orb product-orb--two" aria-hidden="true" />

      <div className="product-experience__heading">
        <div>
          <p className="eyebrow">The app experience</p>
          <h2 id="product-experience-title">From “that looks good” to a real visit.</h2>
        </div>
        <p>Visitors can discover a relevant local offer, redeem it with one clear action and show the confirmation on site. Partners stay in control behind the scenes.</p>
      </div>

      <div className="product-stage">
        <div className="phone-demo" aria-label="Interactive example of the TravelHeroes visitor app">
          <div className="phone-float phone-float--top"><span>✦</span> Discovered nearby</div>
          <div className={`phone ${redeemed ? "phone--redeemed" : ""}`}>
            <div className="phone-island" aria-hidden="true" />
            <div className="phone-screen">
              <header className="app-header">
                <img src="/travelheroes-logo.svg" alt="TravelHeroes" />
                <span aria-hidden="true">•••</span>
              </header>
              <div className="offer-image">
                <img src="/images/cyprus-meze.jpg" alt="Fresh fish mezze served in Cyprus" />
                <span className="offer-tag">Taste</span>
                <button type="button" className="save-button" aria-label="Save this example offer">♡</button>
              </div>
              <div className="offer-body">
                <div className="concept-label">Example offer</div>
                <div className="offer-meta"><span>Polis · 1.2 km</span><span>★ 4.9</span></div>
                <h3>A taste of the coast</h3>
                <p>Enjoy a complimentary local dessert with any fish mezze.</p>
                <div className="offer-perks"><span>Local favourite</span><span>Valid today</span></div>
                <button
                  type="button"
                  className={`redeem-slider ${redeemed ? "is-redeemed" : ""}`}
                  aria-pressed={redeemed}
                  onClick={toggleRedemption}
                >
                  <span className="slider-knob" aria-hidden="true">{redeemed ? "✓" : "→"}</span>
                  <span>{redeemed ? "Redeemed — show partner" : "Swipe to redeem"}</span>
                </button>
                <small className="try-note">Tap the slider to try it</small>
              </div>
            </div>
          </div>
          <div className={`phone-float phone-float--bottom ${redeemed ? "is-visible" : ""}`} aria-live="polite">
            <span>✓</span> Redemption recorded
          </div>
        </div>

        <div className="dashboard-demo">
          <div className="dashboard-topbar">
            <div><span className="status-dot" />Partner dashboard</div>
            <span className="concept-label concept-label--dark">Illustrative preview</span>
          </div>
          <div className="dashboard-welcome">
            <div><small>Good morning</small><h3>Your offer is getting noticed.</h3></div>
            <span className="dashboard-avatar">CP</span>
          </div>
          <div className="metric-grid" aria-live="polite" aria-label="Illustrative partner metrics">
            <article><span>Offer views</span><strong key={`views-${metricPulse}`} className="metric-value">{metrics.views.toLocaleString("en-GB")}</strong><small className="metric-up">↗ 18%</small></article>
            <article><span>Saves</span><strong key={`saves-${metricPulse}`} className="metric-value">{metrics.saves}</strong><small>Visitor intent</small></article>
            <article><span>Redemptions</span><strong key={`redemptions-${metricPulse}`} className="metric-value">{metrics.redemptions}</strong><small className="metric-up">↗ 12%</small></article>
            <article><span>Conversion</span><strong key={`conversion-${metricPulse}`} className="metric-value">{metrics.conversion.toFixed(1)}%</strong><small>View to visit</small></article>
          </div>
          <div className="dashboard-lower">
            <div className="activity-card">
              <div className="mini-heading"><strong>Visitor activity</strong><span>Last 7 days</span></div>
              <div className="activity-chart" aria-label="Illustrative seven-day redemption chart">
                {activity.map((height, index) => <i key={`${index}-${index === activity.length - 1 ? metricPulse : 0}`} className={index === activity.length - 1 ? "activity-latest" : ""} style={{ "--activity": `${height}%` } as CSSProperties} />)}
              </div>
              <div className="chart-days"><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span></div>
            </div>
            <div className="offer-controls">
              <div className="mini-heading"><strong>Offer controls</strong><span>Edit anytime</span></div>
              <div className="control-row"><span><b>Offer status</b><small>Visible to visitors</small></span><i className="toggle is-on" aria-label="Active" /></div>
              <div className="control-row"><span><b>Daily limit</b><small>Keep service comfortable</small></span><em>25</em></div>
              <div className="control-row"><span><b>Valid days</b><small>Change around busy periods</small></span><em>Mon–Fri</em></div>
            </div>
          </div>
          <p className="illustrative-note">Illustrative interface and sample figures — final features and reporting may evolve.</p>
        </div>
      </div>

      <div className="redemption-flow" aria-label="How visitor redemption works">
        <article><span className="flow-icon">01</span><div><h3>Discover</h3><p>A visitor finds your business through a relevant, visual offer.</p></div></article>
        <i aria-hidden="true">→</i>
        <article><span className="flow-icon">02</span><div><h3>Swipe on site</h3><p>The offer is activated when the visitor is ready to redeem with you.</p></div></article>
        <i aria-hidden="true">→</i>
        <article><span className="flow-icon">03</span><div><h3>See the signal</h3><p>The partner can track interest and redemptions in one simple view.</p></div></article>
      </div>

      <div className="partner-control-grid">
        <article className="partner-control-card partner-control-card--image">
          <img src="/images/cyprus-village.jpg" alt="Traditional village architecture in Cyprus" loading="lazy" />
          <div><span>Stay relevant</span><h3>Adapt to the rhythm of your business.</h3></div>
        </article>
        <article className="partner-control-card"><span className="control-icon">↻</span><h3>Change offers flexibly</h3><p>Refresh the value, wording or availability as seasons, capacity and priorities change.</p></article>
        <article className="partner-control-card"><span className="control-icon">◷</span><h3>Choose availability</h3><p>Set valid days and limits so the offer works for your operation, not against it.</p></article>
        <article className="partner-control-card"><span className="control-icon">⌁</span><h3>Learn what connects</h3><p>Use clear signals such as views, saves and redemptions to understand visitor interest.</p></article>
      </div>
    </section>
  );
}

function DistributionVisualStory() {
  const moments = [
    ["Before", "Pre-trip communication", "Inspire"],
    ["Arrival", "Welcome or information point", "Activate"],
    ["Onward", "Mobility and visitor services", "Discover"],
  ] as const;

  return (
    <section className="host-visual-story" aria-labelledby="host-visual-title" data-reveal>
      <div className="stay-planner">
        <img src="/images/cyprus-village.jpg" alt="Traditional village architecture in Cyprus" loading="lazy" />
        <div className="stay-planner__shade" />
        <div className="stay-planner__top"><span>Visitor journey</span><strong>Make discovery easy to enter.</strong></div>
        <div className="stay-planner__cards">
          {moments.map(([time, title, label], index) => (
            <article key={title} style={{ "--delay": `${index * 120}ms` } as CSSProperties}>
              <time>{time}</time><div><strong>{title}</strong><small>{label}</small></div><span aria-hidden="true">↗</span>
            </article>
          ))}
        </div>
      </div>

      <div className="stay-insight">
        <p className="eyebrow">Reach at the right moment</p>
        <h2 id="host-visual-title">Place TravelHeroes where visitor attention already exists.</h2>
        <p>An introduction is most useful when it fits naturally into the journey. TravelHeroes can activate through pre-trip communication, arrival points, mobility services and other trusted tourism channels.</p>
        <div className="stay-insight__stat">
          <div><strong>4.04m</strong><span>tourist arrivals in Cyprus during 2024</span></div>
          <a href={tourismSource} target="_blank" rel="noreferrer">Official CYSTAT data</a>
        </div>
        <div className="curiosity-chart" aria-label="Illustrative pattern of visitor activation across the journey">
          <div className="mini-heading"><strong>Where activation can happen</strong><span>Illustrative journey pattern</span></div>
          <div className="curiosity-bars">
            {[34, 48, 72, 56, 83, 94, 68].map((height, index) => <i key={index} style={{ "--curiosity": `${height}%`, "--delay": `${index * 80}ms` } as CSSProperties} />)}
          </div>
          <div className="curiosity-times"><span>Plan</span><span>Book</span><span>Arrive</span><span>Move</span><span>Explore</span></div>
        </div>
      </div>
    </section>
  );
}

function VisitorOpportunity() {
  return (
    <section className="visitor-opportunity" aria-labelledby="visitor-opportunity-title" data-reveal>
      <div className="visitor-opportunity__intro">
        <p className="eyebrow">The opportunity in motion</p>
        <h2 id="visitor-opportunity-title">Millions arrive. Every journey creates an activation moment.</h2>
        <p>TravelHeroes is designed for the moments when visitors already receive trusted information—before travel, on arrival, in transit and through established tourism channels.</p>
        <a className="inline-cta" href="/distribution-partners">See the opportunity for Distribution Partners</a>
      </div>
      <div className="opportunity-funnel" role="img" aria-label="Official 2024 Cyprus tourism figures showing 4.04 million arrivals, approximately 3.28 million holiday-purpose visits and 3.21 billion euro tourism revenue">
        <div className="funnel-glow" aria-hidden="true" />
        <article className="funnel-step funnel-step--one"><span>01</span><strong>4.04m</strong><p>tourist arrivals</p><small>Official 2024 figure</small></article>
        <div className="funnel-connector"><i /></div>
        <article className="funnel-step funnel-step--two"><span>02</span><strong>≈3.28m</strong><p>holiday-purpose visits</p><small>Derived from 81.1%</small></article>
        <div className="funnel-connector"><i /></div>
        <article className="funnel-step funnel-step--three"><span>03</span><strong>€3.21bn</strong><p>tourism revenue</p><small>Official 2024 figure</small></article>
        <div className="funnel-destination"><b>✦</b><span>More moments to discover local</span></div>
      </div>
      <p className="data-source visitor-opportunity__source">Arrival, purpose and revenue data: <a href={tourismSource} target="_blank" rel="noreferrer">CYSTAT Tourism Statistics 2024</a>. Holiday-purpose volume is calculated from rounded published figures.</p>
    </section>
  );
}

function JoinSignal({ audience }: { audience: Audience }) {
  const isDistribution = audience === "distribution";
  return (
    <section className={`join-signal join-signal--${audience}`} data-reveal>
      <div className="join-signal__visual">
        <img src={isDistribution ? "/images/cyprus-coast.jpg" : "/images/cyprus-meze.jpg"} alt={isDistribution ? "Cyprus coastline viewed from above" : "A local Cyprus fish mezze"} loading="lazy" />
        <span>{isDistribution ? "Your reach" : "Your business"}</span>
      </div>
      <div className="join-signal__copy">
        <p className="eyebrow">Be part of the journey</p>
        <h2>{isDistribution ? "Turn trusted reach into more local discovery." : "Be there when inspiration becomes a plan."}</h2>
        <p>{isDistribution ? "Give visitors simple access through channels they already use while strengthening the Cyprus destination experience." : "Bring a clear, useful offer into the visitor journey and learn what turns interest into a real visit."}</p>
        <div className="join-signal__benefits">
          <span>✓ Curated fit</span><span>✓ Flexible setup</span><span>✓ Clear conversation first</span>
        </div>
        <a href="#contact">Start the conversation</a>
      </div>
    </section>
  );
}

function ContactForm({ audience, intro }: { audience: Audience; intro: string }) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const isDistribution = audience === "distribution";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("sending");
    const form = event.currentTarget;
    const data = new FormData(form);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      if (!response.ok) throw new Error("Submission failed");
      form.reset();
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-title">
      <div className="contact-copy">
        <p className="eyebrow">Join the journey</p>
        <h2 id="contact-title">{isDistribution ? "Could your visitor channel activate more discovery?" : "Could your next visitor find you here?"}</h2>
        <p>{intro}</p>
        <div className="contact-reassurance"><span>✓ Clear next steps</span><span>✓ Start with a fit conversation</span><span>✓ No commitment at this stage</span></div>
      </div>
      <form
        className="contact-form"
        name={`${audience}-contact`}
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={submit}
      >
        <input type="hidden" name="form-name" value={`${audience}-contact`} />
        <input type="hidden" name="audience" value={audience} />
        <p className="honeypot">
          <label>Leave this field empty: <input name="bot-field" /></label>
        </p>
        <label>
          Your name
          <input name="name" type="text" autoComplete="name" required />
        </label>
        <label>
          Organisation
          <input name="organisation" type="text" autoComplete="organization" required />
        </label>
        <label>
          Email address
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          Message
          <textarea name="message" rows={5} required />
        </label>
        <label className="consent">
          <input name="contact-consent" type="checkbox" required />
          <span>I agree that TravelHeroes may contact me about this enquiry.</span>
        </label>
        <button type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : isDistribution ? "Explore becoming a Distribution Partner" : "Explore becoming a Local Partner"}
        </button>
        <p className="form-status" role="status" aria-live="polite">
          {status === "sent" && "Thank you. Your enquiry has been sent."}
          {status === "error" && "Something went wrong. Please try again."}
        </p>
      </form>
    </section>
  );
}

function HomePage() {
  return (
    <main id="top">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="hero-showcase">
          <div className="hero-intro">
            <p className="eyebrow">TravelHeroes Cyprus</p>
            <h1 id="home-title">Connect visitors with more of Cyprus.</h1>
            <p className="lede">
              TravelHeroes brings together organisations that reach visitors and local businesses that give them more reasons to explore.
            </p>
          </div>
          <figure className="hero-photo">
            <img src="/images/cyprus-coast.jpg" alt="Aerial view of the Cyprus coastline" />
            <figcaption><span>Discover more</span><strong>One introduction can unlock the island.</strong></figcaption>
          </figure>
        </div>

        <div className="audience-grid" aria-label="Explore TravelHeroes">
          <article className="audience-card audience-card--navy">
            <p className="card-label">For Distribution Partners</p>
            <h2>Turn trusted reach into visitor activation.</h2>
            <p>For airlines, airports, tourism bodies, mobility providers and other organisations that connect with visitors.</p>
            <a className="text-link" href="/distribution-partners">Learn more <span aria-hidden="true">→</span></a>
          </article>

          <article className="audience-card audience-card--bright">
            <p className="card-label">For Local Partners</p>
            <h2>Bring your business into their journey.</h2>
            <p>For attractions, experiences, food, retail and visitor services that want to be discovered.</p>
            <a className="text-link" href="/partners">Learn more <span aria-hidden="true">→</span></a>
          </article>
        </div>
        <DiscoveryRibbon />
      </section>

      <StatBand />

      <VisitorOpportunity />

      <section className="general-section" aria-labelledby="what-is-title" data-reveal>
        <div>
          <p className="eyebrow">What is TravelHeroes?</p>
          <h2 id="what-is-title">A simpler connection between visitor reach and local discovery.</h2>
        </div>
        <div className="general-copy">
          <p>
            TravelHeroes is designed to help visitors find relevant local places and experiences through trusted channels across their journey.
          </p>
          <p>
            The Cyprus network is being prepared with a focus on useful discovery, carefully selected partners and a straightforward experience for everyone involved.
          </p>
        </div>
      </section>

      <DestinationGallery />

      <section className="principles-section" aria-labelledby="principles-title" data-reveal>
        <div className="section-heading section-heading--compact">
          <p className="eyebrow">Designed to be useful</p>
          <h2 id="principles-title">Less searching. More meaningful discovery.</h2>
        </div>
        <div className="principle-grid">
          <article><span>01</span><h3>Trusted reach</h3><p>Discovery begins through channels visitors already use before arrival and across their trip.</p></article>
          <article><span>02</span><h3>Curated relevance</h3><p>The focus stays on local options that make sense for people visiting Cyprus.</p></article>
          <article><span>03</span><h3>Simple access</h3><p>The journey is designed to move quickly from introduction to useful inspiration.</p></article>
          <article><span>04</span><h3>Shared local value</h3><p>Distribution partners, local businesses and visitors all benefit when more of the destination is discovered.</p></article>
        </div>
        <div className="dual-cta">
          <p>See how TravelHeroes can fit your role in the visitor journey.</p>
          <div><a href="/distribution-partners">For Distribution Partners</a><a href="/partners">For Local Partners</a></div>
        </div>
      </section>
    </main>
  );
}

function ExplainerPage({ audience }: { audience: Audience }) {
  const content = pageContent[audience];
  return (
    <main id="top">
      <section className={`page-hero page-hero--${audience}`} aria-labelledby="page-title">
        <p className="eyebrow">{content.eyebrow}</p>
        <h1 id="page-title">{content.title}</h1>
        <p className="lede">{content.intro}</p>
        <a className="button-link" href="#contact">Contact us now</a>
      </section>

      {audience === "distribution" && <DistributionPartnerSelector />}

      <section className="benefit-grid" aria-label={`${content.eyebrow} benefits`} data-reveal>
        {content.points.map(([title, description], index) => (
          <article key={title}>
            <span>0{index + 1}</span>
            <h2>{title}</h2>
            <p>{description}</p>
          </article>
        ))}
      </section>

      <DataStory audience={audience} />

      {audience === "distribution" && <DistributionVisualStory />}
      {audience === "partners" && <PartnerProductExperience />}

      <section className={`journey-section journey-section--${audience}`} aria-labelledby="journey-title" data-reveal>
        <div className="section-heading">
          <p className="eyebrow">{content.journey.eyebrow}</p>
          <h2 id="journey-title">{content.journey.title}</h2>
          <p>{content.journey.intro}</p>
        </div>
        <ol className="journey-track">
          {content.journey.stages.map(([number, title, description]) => (
            <li key={title}>
              <span>{number}</span>
              <div><h3>{title}</h3><p>{description}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="detail-section" aria-labelledby="detail-title" data-reveal>
        <div className="detail-intro">
          <p className="eyebrow">{content.detail.eyebrow}</p>
          <h2 id="detail-title">{content.detail.title}</h2>
        </div>
        <div className="detail-grid">
          {content.detail.items.map(([title, description]) => (
            <article key={title}><span aria-hidden="true">✓</span><div><h3>{title}</h3><p>{description}</p></div></article>
          ))}
        </div>
      </section>

      <section className="process-section" aria-labelledby="process-title" data-reveal>
        <div>
          <p className="eyebrow">How it works</p>
          <h2 id="process-title">Start with the right fit.</h2>
        </div>
        <ol>
          {content.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </section>

      <JoinSignal audience={audience} />

      <ContactForm audience={audience} intro={content.formIntro} />
    </main>
  );
}

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const audience: Audience | null = path === "/distribution-partners" || path === "/hosts" ? "distribution" : path === "/partners" ? "partners" : null;

  useEffect(() => {
    document.title = audience
      ? `${pageContent[audience].eyebrow} — TravelHeroes Cyprus`
      : "TravelHeroes Cyprus";
  }, [audience]);

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>("[data-reveal]");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [audience]);

  return (
    <>
      <Header />
      {audience ? <ExplainerPage audience={audience} /> : <HomePage />}
      <Footer />
    </>
  );
}
